import DOMPurify from 'dompurify';

/**
 * Sanityzacja treści CMS (WYSIWYG, opisy, snippety) przed wstrzyknięciem jej do DOM.
 *
 * AT-9147: treść bloków tekstowych najemcy szła przez `bypassSecurityTrustHtml()` bez żadnej
 * sanityzacji, więc `<img src=x onerror=…>` wykonywał się u KAŻDEGO odwiedzającego stronę
 * najemcy (stored XSS). Mechanizm jest ten sam co w adminfront (AT-9122): DOMPurify, bo
 * sanitizer Angulara wycina atrybut `style`, a edytor Quill zapisuje w nim kolory
 * i formatowanie.
 *
 * Dwa profile:
 *  - `sanitizeCmsHtml` — treść opisowa; `<iframe>` tylko z serwisów wideo (YouTube, Vimeo),
 *    bo wstawia je nasz własny blot Quilla; formularze zabronione (fałszywy formularz
 *    logowania wysyłający dane na obcy adres).
 *  - `sanitizeIframeEmbed` — blok „iframe" (osadzenie dowolnej treści to funkcja produktu):
 *    dopuszczamy dowolny host, ale wyłącznie `https:` i bez `srcdoc`/atrybutów zdarzeń.
 *
 * Bez DOM-u (SSR, brak `window`) funkcje zwracają pusty łańcuch — nie przepuszczamy
 * nieprzetworzonej treści.
 */

const ALLOWED_IFRAME_HOSTS = [
  'youtube.com',
  'www.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
  'vimeo.com',
];

/** `target` nie należy do domyślnych atrybutów DOMPurify, a linki z `_blank` są w treści normalne. */
const EXTRA_ATTRIBUTES = ['target', 'allowfullscreen', 'frameborder', 'scrolling', 'referrerpolicy'];

const FORBIDDEN_FORM_TAGS = [
  'form', 'input', 'button', 'textarea', 'select', 'option', 'optgroup', 'label', 'fieldset', 'legend', 'datalist',
];

/** `allow` jest wymuszany w haku, `sandbox`/`srcdoc` nigdy nie pochodzą z treści. */
const FORBIDDEN_ATTRIBUTES = ['action', 'formaction', 'formmethod', 'formtarget', 'allow', 'srcdoc'];

/** Stała polityka uprawnień osadzonych odtwarzaczy — autor treści nie może jej zmienić. */
const EMBED_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';

type Purifier = ReturnType<typeof DOMPurify>;

let contentPurifier: Purifier | null = null;
let embedPurifier: Purifier | null = null;

function parseUrl(value: string | null): URL | null {
  if (!value) {
    return null;
  }

  try {
    return new URL(value, window.location.origin);
  } catch {
    return null;
  }
}

/**
 * Linki otwierane w nowej karcie nie mogą dostać dostępu do `window.opener`.
 */
function hardenBlankTargets(node: Element): void {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
}

function getContentPurifier(): Purifier | null {
  if (typeof window === 'undefined') {
    return null;
  }

  if (contentPurifier === null) {
    const purifier = DOMPurify(window);

    purifier.addHook('afterSanitizeAttributes', (node: Element) => {
      hardenBlankTargets(node);

      if (node.tagName !== 'IFRAME') {
        return;
      }

      const url = parseUrl(node.getAttribute('src'));
      if (!url || url.protocol !== 'https:' || !ALLOWED_IFRAME_HOSTS.includes(url.hostname)) {
        node.remove();
        return;
      }

      node.setAttribute('allow', EMBED_ALLOW);
    });

    contentPurifier = purifier;
  }

  return contentPurifier;
}

function getEmbedPurifier(): Purifier | null {
  if (typeof window === 'undefined') {
    return null;
  }

  if (embedPurifier === null) {
    const purifier = DOMPurify(window);

    purifier.addHook('afterSanitizeAttributes', (node: Element) => {
      if (node.tagName !== 'IFRAME') {
        return;
      }

      const url = parseUrl(node.getAttribute('src'));
      if (!url || url.protocol !== 'https:') {
        node.remove();
        return;
      }

      // AT-9147: `allow` z treści (np. `camera; microphone`) wywoływał monity uprawnień
      // w imieniu obcego osadzenia — polityka jest stała, tak samo jak w profilu treści.
      node.setAttribute('allow', EMBED_ALLOW);
    });

    embedPurifier = purifier;
  }

  return embedPurifier;
}

export function sanitizeCmsHtml(html: unknown): string {
  if (typeof html !== 'string' || html === '') {
    return '';
  }

  const purifier = getContentPurifier();
  if (purifier === null) {
    return '';
  }

  return purifier.sanitize(html, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: EXTRA_ATTRIBUTES,
    FORBID_TAGS: FORBIDDEN_FORM_TAGS,
    FORBID_ATTR: FORBIDDEN_ATTRIBUTES,
  });
}

/**
 * Treść bloku „iframe": dowolny host, ale tylko `https:` i bez wykonywalnych atrybutów.
 */
export function sanitizeIframeEmbed(html: unknown): string {
  if (typeof html !== 'string' || html === '') {
    return '';
  }

  const purifier = getEmbedPurifier();
  if (purifier === null) {
    return '';
  }

  return purifier.sanitize(html, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: EXTRA_ATTRIBUTES,
    FORBID_TAGS: FORBIDDEN_FORM_TAGS,
    FORBID_ATTR: FORBIDDEN_ATTRIBUTES,
  });
}
